"use client";

import { Lock, ShieldCheck, CircleCheck as CheckCircle2, Zap, Loader } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RangeInput } from "@/components/ui/range-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
const trustCallouts = [{
  icon: ShieldCheck,
  title: "Legal Compliance",
  description: "PROFECO-compliant and SAT registered."
}, {
  icon: CheckCircle2,
  title: "Zero Upfront Cost",
  description: "No commitment until results are delivered."
}, {
  icon: Zap,
  title: "Expert Assessment",
  description: "29 years of resort management expertise."
}];
function CreditForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-primary/40 bg-primary/5 rounded-md">
        <div className="flex items-start gap-4">
          <CheckCircle2 className="size-6 text-primary mt-1 shrink-0" />
          <div className="flex-1">
            <p className="font-default text-xl font-semibold text-foreground mb-2">
              Inquiry Received
            </p>
            <p className="text-muted-foreground mb-6">{successMessage}</p>
            <Button onClick={resetForm} variant="outline" className="rounded-md border-platinum-border hover:border-primary hover:text-primary">
              Submit Another Inquiry
            </Button>
          </div>
        </div>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-12">
      {}
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <span className="size-1.5 rounded-lg bg-primary" />
          <span className="font-mono text-xs uppercase tracking-widest text-primary">
            Personal Details
          </span>
          <div className="flex-1 h-px bg-primary/20" />
        </div>

        <FormField control={control} name="full_name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                Full Name
              </FormLabel>
              <FormControl>
                <Input placeholder="John Smith" className="h-14 border-0 border-b border-border bg-transparent px-0 text-base text-foreground focus-visible:ring-0 focus-visible:border-primary focus-visible:shadow-[0_1px_0_0_var(--primary)] transition-colors rounded-md" {...field} />
              </FormControl>
              <FormDescription className="text-xs text-slate-muted">
                Enter your legal name for contract verification.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="john.smith@luxury.com" className="h-14 border-0 border-b border-border bg-transparent px-0 text-base text-foreground focus-visible:ring-0 focus-visible:border-primary transition-colors rounded-md" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[\d\s()+\-]+$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                WhatsApp / Phone
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+52 624 164 7031" className="h-14 border-0 border-b border-border bg-transparent px-0 text-base text-foreground focus-visible:ring-0 focus-visible:border-primary transition-colors rounded-md" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <span className="size-1.5 rounded-lg bg-primary" />
          <span className="font-mono text-xs uppercase tracking-widest text-primary">
            Asset Information
          </span>
          <div className="flex-1 h-px bg-primary/20" />
        </div>

        <FormField control={control} name="employer" rules={{
        required: "Resort name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                Resort or Property Name
              </FormLabel>
              <FormControl>
                <Input placeholder="Grand Solmar Land's End" className="h-14 border-0 border-b border-border bg-transparent px-0 text-base text-foreground focus-visible:ring-0 focus-visible:border-primary transition-colors rounded-md" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="annual_income" rules={{
        required: "Maintenance fee is required",
        min: {
          value: 0,
          message: "Amount must be positive"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                Annual Maintenance Fee (USD)
              </FormLabel>
              <FormControl>
                <Input type="number" placeholder="2500" className="h-14 border-0 border-b border-border bg-transparent px-0 text-base text-foreground focus-visible:ring-0 focus-visible:border-primary transition-colors rounded-md" {...field} />
              </FormControl>
              <FormDescription className="text-xs italic text-slate-muted">
                Enter your current yearly maintenance obligation.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <span className="size-1.5 rounded-lg bg-primary" />
          <span className="font-mono text-xs uppercase tracking-widest text-primary">
            Management Goals
          </span>
          <div className="flex-1 h-px bg-primary/20" />
        </div>

        <FormField control={control} name="loan_amount" rules={{
        required: "Target yield is required",
        min: {
          value: 0,
          message: "Amount must be positive"
        }
      }} render={({
        field
      }) => <FormItem>
              <div className="flex items-baseline justify-between">
                <FormLabel className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                  Target Annual Rental Yield
                </FormLabel>
                <span className="font-mono text-sm text-primary tabular-nums">
                  ${Number(field.value || 15000).toLocaleString()}
                </span>
              </div>
              <FormControl>
                <div className="pt-3">
                  <RangeInput min={5000} max={100000} step={1000} value={Number(field.value) || 15000} onValueChange={v => field.onChange(v)} className="w-full h-1 bg-border rounded-md appearance-none cursor-pointer accent-primary" style={{
              background: `linear-gradient(to right, var(--primary) 0%, var(--primary) ${(Number(field.value || 15000) - 5000) / 95000 * 100}%, var(--border) ${(Number(field.value || 15000) - 5000) / 95000 * 100}%, var(--border) 100%)`
            }} />
                  <div className="flex justify-between mt-2 font-mono text-[10px] text-muted-foreground">
                    <span>$5,000</span>
                    <span>$100,000</span>
                  </div>
                </div>
              </FormControl>
              <FormDescription className="text-xs text-slate-muted">
                Estimated gross revenue you wish to generate.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="vehicle_of_interest" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                Specific Service Interest <span className="normal-case italic text-muted-foreground/70">(optional)</span>
              </FormLabel>
              <FormControl>
                <Input placeholder="Full Exit Strategy or Rental Optimization" className="h-14 border-0 border-b border-border bg-transparent px-0 text-base text-foreground focus-visible:ring-0 focus-visible:border-primary transition-colors rounded-md" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <div className="pt-4 border-t border-border/50">
        <FormField control={control} name="agree_terms" rules={{
        required: "You must agree to proceed with your assessment"
      }} render={({
        field
      }) => <FormItem>
              <div className="flex items-start gap-3">
                <FormControl>
                  <Checkbox checked={field.value} onCheckedChange={field.onChange} className="mt-1 rounded-md border-platinum-border data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground" />
                </FormControl>
                <div className="flex-1 space-y-2">
                  <FormLabel className="text-sm font-normal text-foreground leading-relaxed cursor-pointer">
                    I agree to the terms and conditions and authorize a preliminary asset review.
                  </FormLabel>
                  <FormDescription className="text-xs italic text-slate-muted">
                    Your data is protected by bank-grade encryption. This inquiry does not constitute a binding legal agreement.
                  </FormDescription>
                  <FormMessage />
                </div>
              </div>
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 border border-destructive/40 bg-destructive/10 rounded-md">
          <p className="text-sm text-destructive">{submitError}</p>
        </div>}

      <div className="space-y-4 pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full h-14 bg-primary text-primary-foreground hover:bg-secondary hover:text-secondary-foreground font-default font-semibold tracking-wide text-base rounded-md transition-all shadow-md">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Processing...
            </> : <>Get Your Exit Assessment →</>}
        </Button>
        <div className="flex items-center justify-center gap-2 font-mono text-[11px] uppercase tracking-widest text-slate-muted">
          <Lock className="size-3" />
          <span>256-Bit SSL Encrypted Security</span>
        </div>
      </div>
    </Form>;
}
export default function BookYourExperience() {
  return <section data-section-id="635" id="book-your-experience" className="bg-background text-foreground py-24 md:py-32 px-4 sm:px-6 lg:px-8">
      <div className="max-w-[980px] mx-auto">
        {}
        <div className="text-center mb-16 md:mb-20">
          <div className="inline-flex items-center gap-2 mb-6">
            <span className="size-2 rounded-lg bg-primary animate-pulse" />
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-primary">
              Consultation
            </span>
          </div>
          <h2 className="font-default font-semibold text-foreground leading-[1.05] tracking-tight mb-6" style={{
          fontSize: "clamp(2.5rem, 5vw, 4rem)"
        }}>
            Secure Your Asset's Future.
          </h2>
          <p className="text-base md:text-lg text-slate-muted max-w-2xl mx-auto leading-relaxed">
            Receive a professional assessment of your property or timeshare portfolio. Our experts provide transparent exit strategies and rental optimization plans with zero upfront costs.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-8 lg:gap-12 items-start">
          {}
          <div className="bg-card border border-platinum-border rounded-md p-8 md:p-12 lg:p-16 shadow-lg">
            <FormProvider formKey="booking_form_hospitality" formId="booking_form_hospitality" sectionName="book-your-experience" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            employer: "",
            annual_income: "",
            loan_amount: 15000,
            vehicle_of_interest: "",
            agree_terms: false
          }} validationRules={{
            full_name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            employer: {
              required: true
            },
            annual_income: {
              required: true,
              min: 0
            },
            loan_amount: {
              required: true,
              min: 0
            },
            agree_terms: {
              required: true
            }
          }} submitText="Submit Assessment Request" successMessage="Thank you. An IMI Elite Management specialist will review your details and contact you within 24 hours." errorMessage="Submission failed. Please check your information and try again.">
              <CreditForm />
            </FormProvider>
          </div>

          {}
          <aside className="lg:sticky lg:top-24">
            <div className="space-y-4">
              <div className="font-mono text-[11px] uppercase tracking-widest text-slate-muted pb-3 border-b border-platinum-border">
                The IMI Elite Standard
              </div>
              {trustCallouts.map((item, i) => {
              const Icon = item.icon;
              return <div key={i} data-index={i} className="flex items-start gap-3 p-4 bg-card border border-platinum-border rounded-md hover:border-primary transition-colors">
                    <div className="shrink-0 size-9 rounded-md bg-primary/10 flex items-center justify-center">
                      <Icon className="size-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-default font-semibold text-sm text-foreground mb-1">
                        {item.title}
                      </p>
                      <p className="text-xs text-slate-muted leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>;
            })}
            </div>
          </aside>
        </div>
      </div>
    </section>;
}